// @ts-ignore
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  Chip,
  SelectChangeEvent,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Helper } from '../../tools/Helper';
import { api_Routes } from '../../tools/api_Routes.js';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onGroupCreated: () => void;
}

interface User {
  id: number;
  name: string;
  avatar?: string;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({
  open,
  onClose,
  onGroupCreated,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'public',
    avatar: null as File | null,
    members: [] as User[],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const { response } = await Helper.Get({
          url: `${api_Routes.Users.ViewAll}`,
          hasToken: true,
        });
        if (response) {
          setUsers(response.data);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        avatar: file,
      }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('type', formData.type);
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      if (formData.avatar) {
        formDataToSend.append('avatar', formData.avatar);
      }
      
      // Send members as an array of IDs
      formData.members.forEach((member, index) => {
        formDataToSend.append(`members[${index}]`, member.id.toString());
      });

      // @ts-ignore
      const { response, message } = await Helper.Post({
        url: api_Routes.UserChat.CreateGroup,
        hasToken: true,
        data: formDataToSend as any,
      });

      if (response) {
        onGroupCreated();
        onClose();
      } else {
        setError(message || 'Failed to create group');
      }
    } catch (err) {
      setError('An error occurred while creating the group');
      console.error('Error creating group:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Group</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <IconButton
                component="label"
                sx={{
                  width: 100,
                  height: 100,
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                {avatarPreview ? (
                  <Avatar
                    src={avatarPreview}
                    sx={{ width: '100%', height: '100%' }}
                  />
                ) : (
                  <AddPhotoAlternateIcon sx={{ fontSize: 40 }} />
                )}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                />
              </IconButton>
            </Box>

            <TextField
              name="name"
              label="Group Name"
              value={formData.name}
              onChange={handleInputChange}
              required
              fullWidth
            />

            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Group Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleSelectChange}
                label="Group Type"
              >
                <MenuItem value="channel">Channel</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              multiple
              options={users}
              getOptionLabel={(option) => option.name}
              value={formData.members}
              onChange={(_, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  members: newValue,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Members"
                  placeholder="Choose members to add"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar src={option.avatar} sx={{ width: 24, height: 24 }}>
                      {option.name[0]}
                    </Avatar>
                    {option.name}({option.username})
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    avatar={<Avatar src={option.avatar}>{option.name[0]}</Avatar>}
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.name || formData.members.length === 0}
          >
            {loading ? 'Creating...' : 'Create Group'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}; 